import {
  BrowserAuthError,
  BrowserConfigurationAuthError,
  InteractionRequiredAuthError,
  type AuthError,
} from '@azure/msal-browser';
import type { AuthErrorKey } from '@/types/auth';

const resolveErrorCode = (error: unknown): string => {
  if (error && typeof error === 'object' && 'errorCode' in error) {
    return String((error as AuthError).errorCode ?? '').toLowerCase();
  }

  return '';
};

const resolveErrorMessage = (error: unknown): string => {
  if (error && typeof error === 'object' && 'message' in error) {
    return String((error as Error).message ?? '').toLowerCase();
  }

  return '';
};

export const toAuthErrorKey = (error: unknown): AuthErrorKey => {
  if (error instanceof BrowserConfigurationAuthError) {
    return 'authConfigurationError';
  }

  if (error instanceof InteractionRequiredAuthError) {
    return 'authSessionExpired';
  }

  if (error instanceof BrowserAuthError) {
    const browserErrorCode = error.errorCode.toLowerCase();

    if (browserErrorCode.includes('interaction_in_progress')) {
      return 'authInteractionInProgress';
    }

    if (browserErrorCode.includes('user_cancelled')) {
      return 'authLoginCancelled';
    }

    if (browserErrorCode.includes('monitor_window_timeout')) {
      return 'authSessionExpired';
    }

    if (browserErrorCode.includes('no_network')) {
      return 'authNetworkError';
    }
  }

  const errorCode = resolveErrorCode(error);
  const errorMessage = resolveErrorMessage(error);

  if (errorCode.includes('interaction_in_progress')) {
    return 'authInteractionInProgress';
  }

  if (
    errorCode.includes('hash_empty_error') ||
    errorMessage.includes('hash_empty_error') ||
    errorCode.includes('state_mismatch')
  ) {
    return 'authSessionExpired';
  }

  if (
    errorCode.includes('user_cancelled') ||
    errorCode.includes('access_denied') ||
    errorMessage.includes('access_denied')
  ) {
    return 'authLoginCancelled';
  }

  if (
    errorCode.includes('network') ||
    errorCode.includes('temporarily_unavailable')
  ) {
    return 'authNetworkError';
  }

  if (
    errorCode.includes('interaction_required') ||
    errorCode.includes('login_required')
  ) {
    return 'authSessionExpired';
  }

  if (errorCode.includes('invalid_grant') || errorCode.includes('token')) {
    return 'authSessionExpired';
  }

  return 'authFailed';
};
